import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import type { Template } from '@/types';

export interface LibraryPageDetails {
  name: string;
  category: string;
  description: string;
}

interface LibraryPageDetailsDialogProps {
  open: boolean;
  /** Leeg: een nieuwe pagina. Anders de pagina waarvan je naam en categorie wijzigt. */
  page: Template | null;
  /** Bestaande categorieën, als suggestie. */
  pages: Template[];
  onOpenChange: (open: boolean) => void;
  onSubmit: (details: LibraryPageDetails) => Promise<void>;
}

export default function LibraryPageDetailsDialog({
  open,
  page,
  pages,
  onOpenChange,
  onSubmit,
}: LibraryPageDetailsDialogProps) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(page?.name ?? '');
    setCategory(page?.category ?? '');
    setDescription(page?.description ?? '');
  }, [open, page]);

  const categories = useMemo(
    () =>
      [...new Set(pages.map((p) => p.category?.trim()).filter((c): c is string => Boolean(c)))].sort((a, b) =>
        a.localeCompare(b, 'nl')
      ),
    [pages]
  );

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onSubmit({ name: name.trim(), category: category.trim(), description: description.trim() });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !saving && onOpenChange(next)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{page ? 'Naam en categorie' : 'Nieuwe pagina'}</DialogTitle>
          <DialogDescription>
            {page
              ? 'Offertes waar deze pagina al in staat, veranderen niet mee.'
              : 'De pagina opent in de editor, zodat je hem meteen kunt vullen.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label htmlFor="libraryDetailsName">Naam</Label>
            <Input
              id="libraryDetailsName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={saving}
              placeholder="Bijv. Over UBO"
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="libraryDetailsCategory">Categorie (optioneel)</Label>
            <Input
              id="libraryDetailsCategory"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              disabled={saving}
              placeholder="Bijv. Werkwijze of Voorwaarden"
              list="libraryDetailsCategories"
            />
            <datalist id="libraryDetailsCategories">
              {categories.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </div>
          <div className="space-y-2">
            <Label htmlFor="libraryDetailsDescription">Beschrijving (optioneel)</Label>
            <textarea
              id="libraryDetailsDescription"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              disabled={saving}
              className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              placeholder="Wanneer gebruik je deze pagina?"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Annuleren
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!name.trim() || saving}>
            {saving ? 'Opslaan...' : page ? 'Opslaan' : 'Aanmaken'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
